import { productImageAlt } from "@/lib/imageAlt";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { formatPrice, getShopifyImageUrl, ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { useCookieBannerVisible } from "@/hooks/useCookieGate";
import { useStickyBuyBar } from "@/stores/stickyBuyBarStore";
import { genuineOriginalPrice } from "@/lib/pricing";
import { variantLabel } from "@/lib/variants";

interface StickyAddToCartProps {
  product: ShopifyProduct["node"];
  selectedVariant: string | null;
  image?: string;
}

/**
 * Mobile-only sticky add-to-cart bar. It clicks the real ADD TO CART button in
 * ProductInfoPanel (marked with data-pdp-add-to-cart) so the variant, quantity
 * and toast are always identical to the main CTA.
 */
export const StickyAddToCart = ({ product, selectedVariant, image }: StickyAddToCartProps) => {
  const [visible, setVisible] = useState(false);
  const isCartLoading = useCartStore((s) => s.isLoading);
  const cookieBannerVisible = useCookieBannerVisible();

  const variants = product.variants?.edges || [];
  const currentVariant = variants.find((v) => v.node.id === selectedVariant)?.node;
  const available = !!currentVariant?.availableForSale;

  // On a first visit the cookie banner is up and the main ADD TO CART starts
  // below the fold of a 390x844 phone (y=927), so hiding under the banner left
  // no price and no buy control on screen. The bar sits on top of the banner
  // (which starts 4rem up), as the cart's bar does.
  const [bannerHeight, setBannerHeight] = useState(0);
  useEffect(() => {
    const banner = cookieBannerVisible ? document.querySelector<HTMLElement>("[data-cookie-banner]") : null;
    setBannerHeight(banner?.offsetHeight ?? 0);
  }, [cookieBannerVisible]);

  useEffect(() => {
    const target = document.querySelector("[data-pdp-add-to-cart]");
    if (!target) {
      // Main CTA not rendered (unavailable variant) — show the disabled bar.
      setVisible(true);
      return;
    }
    // The sticky header covers the top 64px and the bottom nav the last 57px
    // (with the cookie banner up, everything below the banner's top). The
    // button only counts as on screen while all of it is clear of both, as
    // MobileCheckoutBar does: a button 1px into view under the nav, the first
    // view of a long title's page, left no Add to Cart to tap.
    const bottom = bannerHeight ? 64 + bannerHeight : 57;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.intersectionRatio < 1),
      { threshold: 1, rootMargin: `-64px 0px -${bottom}px 0px` }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [selectedVariant, available, bannerHeight]);

  const priceNum = parseFloat(currentVariant?.price.amount ?? "0");
  const shown = visible && (!cookieBannerVisible || bannerHeight > 0) && !!currentVariant && priceNum > 0;
  const setStickyBar = useStickyBuyBar((s) => s.set);
  useEffect(() => {
    setStickyBar("mobile", shown);
    return () => setStickyBar("mobile", false);
  }, [shown, setStickyBar]);

  if (!currentVariant) return null;

  // $0.00 = broken supplier row. Never offer a buy bar for one.
  if (!(priceNum > 0)) return null;
  const originalPrice = genuineOriginalPrice(priceNum, currentVariant.compareAtPrice?.amount, available);
  const showSale = !!originalPrice && originalPrice > priceNum;

  // The exact price, cents included: a rounded "$687" under a "$686.99" panel
  // reads as two prices for one item.
  const currency = currentVariant.price.currencyCode || "USD";
  // Which size or colour that price is for, when there is a choice.
  const selectedLabel = variantLabel(product, currentVariant);

  const handleClick = () => {
    const btn = document.querySelector("[data-pdp-add-to-cart]") as HTMLButtonElement | null;
    btn?.click();
  };

  return (
    <AnimatePresence>
      {shown && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="md:hidden fixed left-0 right-0 z-40 bg-background border-t border-border"
          style={{ bottom: bannerHeight ? `calc(4rem + ${bannerHeight}px)` : "calc(3.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="flex items-center gap-3 px-4 py-2.5">
            {image && (
              <img
                src={getShopifyImageUrl(image, 128)}
                alt={productImageAlt(product.title)}
                className="w-11 h-11 object-contain bg-[#f5f0eb] flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
            )}
            <div className="min-w-0 flex-1 leading-tight">
              {/* One line high: a struck-through price that does not fit wraps out of view. */}
              <div className="flex flex-wrap items-baseline gap-x-1.5 h-[19px] overflow-hidden">
                <span className="text-[15px] font-semibold text-foreground">
                  {formatPrice(currentVariant.price.amount, currency)}
                </span>
                {showSale && (
                  <span className="text-[12px] text-muted-foreground line-through">
                    {formatPrice(String(originalPrice), currency)}
                  </span>
                )}
              </div>
              {selectedLabel && <p className="text-[11px] text-foreground truncate">{selectedLabel}</p>}
              <p className="text-[10px] text-muted-foreground truncate">Free U.S. shipping over $120 · 30-day returns</p>
            </div>
            <button
              onClick={handleClick}
              disabled={!available || isCartLoading}
              className="flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-primary text-primary-foreground text-[12px] tracking-[0.15em] uppercase font-bold transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
            >
              {isCartLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {available ? "ADD TO CART" : "UNAVAILABLE"}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
